import { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  useToast,
  Grid,
  Flex,
  Heading,
  Text,
  InputGroup,
  InputRightAddon,
} from "@chakra-ui/react";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { fetchRoles } from "../../actions/role.actions";
import { createNewUser } from "../../actions/users.action";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Unauthorized } from "../../components/Unauthorized";

const CreateUser = () => {
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [show, setShow] = useState(false);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    dob: "",
    gender: "",
    password: "",
    role: "",
    employeeId: "",
    department: "",
    team: "",
    designation: "",
    dateOfJoining: "",
    branch: "",
    shiftTimings: "",
    emergencyName: "",
    emergencyRelation: "",
    emergencyPhone: "",
    languageKnown: "",
    address: "",
    state: "",
    city: "",
    pincode: "",
    addressProof: null,
    idProof: null,
  });

  const genders = ["Male", "Female", "Other"];
  const shifts = ["9-6", "10-7", "11-8", "12-9"];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.files[0] });
  };

  const getAllRoles = async () => {
    try {
      let data = await fetchRoles();
      setRoles(data || []);
    } catch (err) {
      setError(err?.message || "Unable to fetch roles");
      toast({
        title: "Error",
        description: err?.message || "Error",
        status: "error",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createNewUser(formData);
      toast({
        title: "Success",
        description: "User created",
        status: "success",
      });
      setFormData({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        dob: "",
        gender: "",
        password: "",
        role: "",
        employeeId: "",
        department: "",
        team: "",
        designation: "",
        dateOfJoining: "",
        branch: "",
        shiftTimings: "",
        emergencyName: "",
        emergencyRelation: "",
        emergencyPhone: "",
        languageKnown: "",
        address: "",
        state: "",
        city: "",
        pincode: "",
        addressProof: null,
        idProof: null,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Something went wrong",
        status: "error",
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    getAllRoles();
  }, []);

  if (error === "Unauthorized user") return <Unauthorized />;

  const Section = ({ title, step, children }) => (
    <Box
      bg="white"
      p={6}
      borderRadius="2xl"
      mb={6}
      border="1px solid #D0D5DD"
      transition="box-shadow 150ms ease, transform 150ms ease"
      _hover={{
        boxShadow: "0 8px 24px rgba(15, 96, 255, 0.08)",
        transform: "translateY(-2px)",
      }}
    >
      <Flex justify="space-between" mb={4}>
        <Heading size="md">{title}</Heading>
        <Text color="gray.500">Step {step} of 4</Text>
      </Flex>
      {children}
    </Box>
  );

  return (
    <Box
      p={6}
      sx={{
        "input, select": {
          height: "40px",
          transition: "none !important",
          userSelect: "text",
          cursor: "text",
        },
      }}
    >
      <BreadCrumbMaker
        items={[
          { link: "/settings", title: "Access Control" },
          { link: "#", title: "Add New User", on: true },
        ]}
      />

      <form onSubmit={handleSubmit}>
        {/* USER DETAILS */}
        <Section title="User Details" step="1">
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }}
            gap={4}
            alignItems="flex-start"
          >
            <FormControl key="firstName">
              <FormLabel>First Name</FormLabel>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="lastName">
              <FormLabel>Last Name</FormLabel>
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="phoneNumber">
              <FormLabel>Mobile Number</FormLabel>
              <Input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="email">
              <FormLabel>Email ID</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="dob">
              <FormLabel>Date of Birth</FormLabel>
              <Input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="gender">
              <FormLabel>Gender</FormLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                {genders.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl key="password">
              <FormLabel>Password</FormLabel>
              <InputGroup pointerEvents="auto">
                <Input
                  type={show ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <InputRightAddon
                  onClick={() => setShow(!show)}
                  cursor="pointer"
                >
                  {show ? <FaRegEyeSlash /> : <FaRegEye />}
                </InputRightAddon>
              </InputGroup>
            </FormControl>

            <FormControl key="role">
              <FormLabel>Role</FormLabel>
              <Select name="role" value={formData.role} onChange={handleChange}>
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.roleName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Section>

        {/* EMPLOYMENT DETAILS */}
        <Section title="Employment Details" step="2">
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4,1fr)" }}
            gap={4}
            alignItems="flex-start"
          >
            <FormControl key="employeeId">
              <FormLabel>Employee ID</FormLabel>
              <Input
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="department">
              <FormLabel>Department</FormLabel>
              <Input
                name="department"
                value={formData.department}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="team">
              <FormLabel>Team</FormLabel>
              <Input
                name="team"
                value={formData.team}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="designation">
              <FormLabel>Designation</FormLabel>
              <Input
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="dateOfJoining">
              <FormLabel>Date of Joining</FormLabel>
              <Input
                type="date"
                name="dateOfJoining"
                value={formData.dateOfJoining}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="branch">
              <FormLabel>Branch</FormLabel>
              <Input
                name="branch"
                value={formData.branch}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="shiftTimings">
              <FormLabel>Shift Timings</FormLabel>
              <Select
                name="shiftTimings"
                value={formData.shiftTimings}
                onChange={handleChange}
              >
                <option value="">Select Shift</option>
                {shifts.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Section>

        {/* EMERGENCY */}
        <Section title="Emergency Contact & Languages" step="3">
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3,1fr)" }}
            gap={4}
            alignItems="flex-start"
          >
            <FormControl key="emergencyName">
              <FormLabel>Emergency Contact Name</FormLabel>
              <Input
                name="emergencyName"
                value={formData.emergencyName}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="emergencyRelation">
              <FormLabel>Relation</FormLabel>
              <Input
                name="emergencyRelation"
                value={formData.emergencyRelation}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="emergencyPhone">
              <FormLabel>Contact Number</FormLabel>
              <Input
                name="emergencyPhone"
                value={formData.emergencyPhone}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="languageKnown">
              <FormLabel>Language Known</FormLabel>
              <Input
                name="languageKnown"
                value={formData.languageKnown}
                onChange={handleChange}
              />
            </FormControl>
          </Grid>
        </Section>

        {/* ADDRESS */}
        <Section title="Address & Documents" step="4">
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }}
            gap={4}
            alignItems="flex-start"
          >
            <FormControl key="address">
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="state">
              <FormLabel>State</FormLabel>
              <Input
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="city">
              <FormLabel>City</FormLabel>
              <Input
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl key="pincode">
              <FormLabel>Pincode</FormLabel>
              <Input
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
              />
            </FormControl>
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }}
            gap={8}
            mt={6}
          >
            <Box
              p={4}
              border="2px dashed #d0d7e2"
              borderRadius="lg"
              textAlign="center"
            >
              <Heading size="sm" mb={2}>
                Upload Address Proof
              </Heading>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Upload PNG, JPG, or PDF (Max 5MB)
              </Text>

              <Input
                type="file"
                accept="image/*,.pdf"
                name="addressProof"
                onChange={handleFileChange}
                display="none"
                id="addressProof"
              />
              <label htmlFor="addressProof">
                <Box cursor="pointer" p={4} borderRadius="md" bg="gray.50">
                  Drag or click to upload
                </Box>
              </label>
            </Box>

            <Box
              p={4}
              border="2px dashed #d0d7e2"
              borderRadius="lg"
              textAlign="center"
            >
              <Heading size="sm" mb={2}>
                Upload ID Proof
              </Heading>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Upload PNG, JPG, or PDF (Max 5MB)
              </Text>

              <Input
                type="file"
                accept="image/*,.pdf"
                name="idProof"
                onChange={handleFileChange}
                display="none"
                id="idProof"
              />
              <label htmlFor="idProof">
                <Box cursor="pointer" p={4} borderRadius="md" bg="gray.50">
                  Drag or click to upload
                </Box>
              </label>
            </Box>
          </Grid>
        </Section>

        <Button
          colorScheme="blue"
          size="lg"
          mt={4}
          type="submit"
          isLoading={loading}
        >
          Submit
        </Button>
      </form>
    </Box>
  );
};

export default CreateUser;
